import React from 'react';

/**
 * JIRA Workflow Progress Bar Component
 * Displays the current status in the JIRA workflow as a horizontal progress bar
 */
const JiraProgressBar = ({ status, jiraData = {}, className = '', style = {} }) => {
  // Define the JIRA workflow stages in order (matching actual JIRA statuses)
  const workflowStages = [
    { key: 'NEW', label: 'NEW', color: '#6b7280', bgColor: '#f3f4f6' },
    { key: 'READY FOR DEVELOPMENT', label: 'READY FOR DEVELOPMENT', color: '#3b82f6', bgColor: '#dbeafe' },
    { key: 'IN DEVELOPMENT', label: 'IN DEVELOPMENT', color: '#f59e0b', bgColor: '#fef3c7' },
    { key: 'IN CODE REVIEW', label: 'IN CODE REVIEW', color: '#8b5cf6', bgColor: '#ede9fe' },
    { key: 'READY FOR TESTING', label: 'READY FOR TESTING', color: '#06b6d4', bgColor: '#cffafe' },
    { key: 'IN TESTING', label: 'IN TESTING', color: '#10b981', bgColor: '#d1fae5' },
    { key: 'READY FOR RELEASE', label: 'READY FOR RELEASE', color: '#059669', bgColor: '#a7f3d0' }
  ];

  // JIRA status mappings based on actual workflow
  const statusMappings = {
    // Stage 0: NEW
    'NEW': 0,
    
    // Stage 1: READY FOR DEVELOPMENT
    'READY FOR DEVELOPMENT': 1,
    
    // Stage 2: IN DEVELOPMENT
    'IN DEVELOPMENT': 2,
    
    // Stage 3: IN CODE REVIEW
    'IN CODE REVIEW': 3,
    
    // Stage 4: READY FOR TESTING
    'READY FOR TESTING': 4,
    
    // Stage 5: IN TESTING
    'IN TESTING': 5,
    
    // Stage 6: READY FOR RELEASE
    'READY FOR RELEASE': 6
  };

  // First try direct mapping
  let currentStageIndex = statusMappings[status?.toUpperCase()] ?? -1;
  
  // If no direct mapping, try fuzzy matching
  if (currentStageIndex === -1) {
    currentStageIndex = workflowStages.findIndex(stage => {
      const statusUpper = (status || '').toUpperCase();
      const stageKeyUpper = stage.key.toUpperCase();
      const stageLabelUpper = stage.label.toUpperCase();
      
      // Exact matches
      if (statusUpper === stageKeyUpper || statusUpper === stageLabelUpper) {
        return true;
      }
      
      // Partial matches for common variations
      const statusVariations = [
        'IN DEVELOPMENT', 'IN PROGRESS', 'DEVELOPMENT', 'DEV',
        'BACKLOG', 'REVIEW', 'TESTING', 'DONE', 'COMPLETE'
      ];
      
      for (const variation of statusVariations) {
        if (statusUpper.includes(variation) && stageKeyUpper.includes(variation)) {
          return true;
        }
      }
      
      // Check if status contains any part of the stage
      const stageWords = stageKeyUpper.split(' ');
      const statusWords = statusUpper.split(' ');
      
      // If any word from stage is in status, it's a match
      for (const stageWord of stageWords) {
        if (stageWord.length > 2 && statusWords.some(statusWord => 
          statusWord.includes(stageWord) || stageWord.includes(statusWord)
        )) {
          return true;
        }
      }
      
      return false;
    });
  }

  // Calculate progress percentage (0-100)
  // NEW = 0%, DONE = 86%, only 100% when deployed to PROD
  let progressPercentage = currentStageIndex >= 0 
    ? (currentStageIndex / workflowStages.length) * 100 
    : 0;

  // Check deployment environment and set progress accordingly
  const deployedEnv = jiraData.deployedEnv;
  const isDeployedToProd = deployedEnv === 'Production' || 
                          deployedEnv === 'PROD' ||
                          deployedEnv === 'prod' ||
                          deployedEnv === 'Prod';
  const isPreProd = deployedEnv === 'Pre-Prod' || 
                   deployedEnv === 'PRE-PROD' ||
                   deployedEnv === 'pre-prod';
  
  // Set progress to 100% only when deployed to production
  if (isDeployedToProd && currentStageIndex >= workflowStages.length - 1) {
    progressPercentage = 100;
  }

  // Debug logging removed to reduce console spam

  // Get current stage info
  const currentStage = currentStageIndex >= 0 ? workflowStages[currentStageIndex] : null;

  return (
    <div 
      className={`jira-progress-bar ${className}`}
      style={{
        width: '100%',
        ...style
      }}
    >
      {/* Progress Bar Container */}
      <div style={{
        width: '100%',
        height: '8px',
        backgroundColor: '#e5e7eb',
        borderRadius: '4px',
        overflow: 'hidden',
        position: 'relative',
        marginBottom: '6px'
      }}>
        {/* Progress Fill */}
        <div style={{
          width: `${progressPercentage}%`,
          height: '100%',
          background: progressPercentage >= 100 
            ? 'linear-gradient(90deg, #3b82f6 0%, #10b981 50%, #059669 100%)' // Blue to green for complete
            : progressPercentage >= 75
            ? 'linear-gradient(90deg, #3b82f6 0%, #10b981 100%)' // Blue to light green
            : progressPercentage >= 50
            ? 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 50%, #10b981 100%)' // Blue to light green
            : progressPercentage >= 25
            ? 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)' // Blue to lighter blue
            : 'linear-gradient(90deg, #1e40af 0%, #3b82f6 100%)', // Dark blue to blue for low progress
          borderRadius: '4px',
          transition: 'width 0.3s ease-in-out, background 0.3s ease-in-out',
          position: 'relative'
        }}>
          {/* Subtle shine effect */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
            borderRadius: '4px 4px 0 0'
          }} />
        </div>
      </div>

      {/* Status Label */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '11px',
        fontWeight: '500'
      }}>
        <span style={{
          color: progressPercentage >= 100 
            ? '#059669' // Green for complete
            : progressPercentage >= 75
            ? '#10b981' // Light green
            : progressPercentage >= 50
            ? '#3b82f6' // Blue
            : progressPercentage >= 25
            ? '#60a5fa' // Lighter blue
            : '#1e40af', // Dark blue for low progress
          fontWeight: '600'
        }}>
          {(() => {
            // Special case: Ready for Release + PROD deployment
            if (status === 'Ready for Release' && isDeployedToProd) {
              const fixVersions = jiraData.versions || [];
              if (fixVersions.length > 0) {
                // Get the most recent fix version
                const latestVersion = fixVersions[fixVersions.length - 1];
                const versionName = latestVersion.name || latestVersion;
                return `Released in ${versionName}`;
              } else {
                return 'Released to Prod';
              }
            }
            
            // Default status display
            return status || currentStage?.label || 'UNKNOWN';
          })()}
        </span>
        
        {/* Progress percentage */}
        <span style={{
          color: '#6b7280',
          fontSize: '10px'
        }}>
          {Math.round(progressPercentage)}%
        </span>
      </div>

      {/* Deployment Environment Display */}
      {deployedEnv && deployedEnv !== 'Unknown' && deployedEnv.trim() !== '' && 
       !(status === 'Ready for Release' && isDeployedToProd) && (
        <div style={{
          marginTop: '4px',
          fontSize: '10px',
          fontWeight: '500'
        }}>
          {isDeployedToProd ? (
            <span style={{
              color: '#059669',
              backgroundColor: '#d1fae5',
              padding: '2px 6px',
              borderRadius: '4px',
              fontWeight: '600'
            }}>
              🚀 Released to Prod
            </span>
          ) : isPreProd ? (
            <a 
              href="https://pre-prod--busable.netlify.app" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                color: '#d97706',
                backgroundColor: '#fef3c7',
                padding: '2px 6px',
                borderRadius: '4px',
                fontWeight: '600',
                textDecoration: 'none',
                cursor: 'pointer',
                display: 'inline-block'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#fde68a';
                e.target.style.color = '#b45309';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#fef3c7';
                e.target.style.color = '#d97706';
              }}
            >
              🚌 Click to pilot test
            </a>
          ) : (
            <span style={{
              color: '#6b7280',
              backgroundColor: '#f3f4f6',
              padding: '2px 6px',
              borderRadius: '4px',
              fontWeight: '500'
            }}>
              📦 Deployed to {deployedEnv}
            </span>
          )}
        </div>
      )}

    </div>
  );
};

export default JiraProgressBar;
